import { useCallback, useEffect, useState } from 'react'
import { type ApiReferral, fetchReferral } from '@/fetcher'
import { formatUsdt } from '@/helpers/format'
import { SectionCard } from './SectionCard'

const StatLine = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-[var(--app-border)] py-3 last:border-b-0">
    <span className="text-sm text-[var(--app-muted)]">{label}</span>
    <span className="text-right text-sm font-semibold text-[var(--app-text)]">
      {value}
    </span>
  </div>
)

const buildShareUrl = (link: string, text: string | null) =>
  `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text ?? 'Присоединяйтесь по моей реферальной ссылке')}`

export const ReferralPage = () => {
  const [referral, setReferral] = useState<ApiReferral | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const loadReferral = useCallback(async () => {
    setIsLoading(true)
    setErrorMessage(null)

    try {
      setReferral(await fetchReferral())
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Не удалось загрузить реферальную статистику',
      )
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadReferral()
  }, [loadReferral])

  if (isLoading) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 text-sm text-[var(--app-muted)]">
        Загрузка реферальной статистики...
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 px-4 py-8">
        <div className="rounded-[18px] bg-[var(--app-danger-soft)] px-4 py-3 text-sm text-[var(--app-text)]">
          {errorMessage}
        </div>
        <button
          type="button"
          onClick={() => void loadReferral()}
          className="text-sm font-medium text-[var(--app-accent)] transition hover:opacity-80"
        >
          Повторить
        </button>
      </div>
    )
  }

  if (!referral?.enabled) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-2xl items-center justify-center px-4 py-8 text-center">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--app-text)]">
            Реферальная программа
          </h1>
          <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-[var(--app-muted)]">
            Вы пока не подключены к реферальной программе. Свяжитесь с
            менеджером, чтобы узнать условия участия.
          </p>
          <a
            className="mt-8 inline-flex min-h-12 items-center justify-center rounded-full bg-[var(--app-accent)] px-5 py-3 text-sm font-semibold text-[var(--app-accent-text)] transition hover:opacity-90"
            href={import.meta.env.BUN_PUBLIC_MANAGER_LINK}
          >
            Написать менеджеру
          </a>
        </div>
      </div>
    )
  }

  const { stats, codes } = referral

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-8">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--app-text)]">
          Реферальная программа
        </h1>
        <p className="mt-2 text-sm text-[var(--app-muted)]">
          Статистика приглашённых пользователей и начислений
        </p>
      </div>

      <SectionCard title="Реферальная статистика">
        <StatLine
          label="Накоплено к выплате"
          value={`${formatUsdt(stats.pending)} USDT`}
        />
        <StatLine
          label="За всё время"
          value={`${formatUsdt(stats.totalObligations)} USDT`}
        />
        <StatLine
          label="Выплачено"
          value={`${formatUsdt(stats.totalPaid)} USDT`}
        />
        <StatLine
          label="Процент"
          value={`${formatUsdt(stats.referralPercent)}%`}
        />
        <StatLine label="Сделок" value={String(stats.dealsCount)} />
        <StatLine
          label="Привлечено пользователей"
          value={String(stats.referredUsersCount)}
        />
      </SectionCard>

      <SectionCard title="Ваши ссылки">
        {codes.length > 0 ? (
          codes.map((code) => (
            <div
              className="rounded-[18px] bg-[var(--app-input)] px-4 py-3"
              key={code.code}
            >
              {code.link ? (
                <a
                  className="break-all text-sm font-medium text-[var(--app-accent)] hover:opacity-80"
                  href={buildShareUrl(code.link, code.text)}
                >
                  {code.link}
                </a>
              ) : (
                <p className="break-all text-sm text-[var(--app-text)]">
                  {code.code}
                </p>
              )}
              {code.label && (
                <p className="mt-1 text-xs text-[var(--app-muted)]">
                  {code.label}
                </p>
              )}
            </div>
          ))
        ) : (
          <p className="text-sm text-[var(--app-muted)]">Нет активных кодов</p>
        )}
      </SectionCard>
    </div>
  )
}
